import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { FileDown, Lock, Save, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const downloadPdf = async (path, filename) => {
  const tid = toast.loading('PDF wird erstellt...');
  try {
    const res = await axios.get(`${API}${path}`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('PDF heruntergeladen', { id: tid });
  } catch (e) {
    let detail = 'PDF konnte nicht erstellt werden';
    if (e.response?.data instanceof Blob) {
      try { detail = JSON.parse(await e.response.data.text()).detail || detail; } catch (_) { /* kein JSON */ }
    }
    toast.error(detail, { id: tid });
  }
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '');

export const TagStatusHinweis = ({ tagDoc }) => {
  if (tagDoc?.abgeschlossen) {
    return (
      <div data-testid="tag-status-abgeschlossen" className="flex items-center gap-2 text-sm px-3 py-2 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800">
        <Lock className="h-4 w-4" /> Tag abgeschlossen durch {tagDoc.abgeschlossen.name} am {fmt(tagDoc.abgeschlossen.am)}. Korrekturen (Anwesenheit oder Thema) sind weiter möglich – nur mit Grund, wird protokolliert.
      </div>
    );
  }
  if (tagDoc?.erfasst_am) {
    return (
      <div data-testid="tag-status-gespeichert" className="text-sm px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-amber-800">
        Gespeichert ({fmt(tagDoc.updated_at)}), noch nicht abgeschlossen. Unterschriften werden erst beim Tagesabschluss übertragen.
      </div>
    );
  }
  return (
    <div data-testid="tag-status-vorausgefuellt" className="text-sm px-3 py-2 rounded-md bg-stone-100 border border-stone-300 text-stone-700">
      Alle als anwesend vorausgefüllt, noch nicht gespeichert. Nur Abweichungen anklicken.
    </div>
  );
};

export const EinwilligungBadge = ({ ew }) => (
  ew ? (
    <span data-testid="badge-einwilligung" title={`Einwilligung e-Unterschrift: ${ew.quelle} (${ew.datum})`}
      className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-emerald-100 text-emerald-800 flex items-center gap-1">
      <ShieldCheck className="h-3 w-3" /> e-Unterschrift
    </span>
  ) : (
    <span data-testid="badge-keine-einwilligung" title="Keine Einwilligung zur elektronischen Unterschrift hinterlegt – es wird keine Unterschrift übertragen"
      className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-stone-200 text-stone-600">ohne e-Unterschrift</span>
  )
);

export const TeilweiseFelder = ({ wert = {}, onChange }) => (
  <div className="flex items-center gap-2 text-xs px-3 pb-2" data-testid="teilweise-felder">
    <span className="text-stone-600">anwesend von</span>
    <input type="time" value={wert.von || ''} onChange={(e) => onChange({ ...wert, von: e.target.value })}
      className="border rounded px-1 py-0.5" data-testid="teilweise-von" />
    <span className="text-stone-600">bis</span>
    <input type="time" value={wert.bis || ''} onChange={(e) => onChange({ ...wert, bis: e.target.value })}
      className="border rounded px-1 py-0.5" data-testid="teilweise-bis" />
    <span className="text-stone-600">Fehl-UE</span>
    <input type="number" min="0.5" step="0.5" value={wert.fehl_ue ?? ''} onChange={(e) => onChange({ ...wert, fehl_ue: e.target.value })}
      className="border rounded px-1 py-0.5 w-16" data-testid="teilweise-fehl-ue" />
  </div>
);

export const TagAktionen = ({ kursId, datum, tagDoc, onSave }) => (
  <div className="flex flex-wrap gap-2 justify-end">
    <Button variant="outline" onClick={() => downloadPdf(`/anwesenheit-tag/tag/${kursId}/${datum}/pdf`, `Anwesenheitsliste_${datum}.pdf`)}
      data-testid="btn-tagesliste-pdf">
      <FileDown className="h-4 w-4 mr-2" /> Tagesliste PDF
    </Button>
    <Button variant="outline" onClick={() => downloadPdf(`/anwesenheit-tag/fehlzeiten/${kursId}/pdf`, `Fehlzeiten_Kurs_${kursId}.pdf`)}
      data-testid="btn-fehlzeiten-kurs-pdf">
      <FileDown className="h-4 w-4 mr-2" /> Fehlzeiten Kurs
    </Button>
    <Button onClick={() => onSave(false)} variant="outline" data-testid="btn-anwesenheit-speichern">
      <Save className="h-4 w-4 mr-2" /> Speichern
    </Button>
    {!tagDoc?.abgeschlossen && (
      <Button onClick={() => onSave(true)} className="bg-[#C2410C] hover:bg-[#9A3412] text-white" data-testid="btn-tag-abschliessen">
        <Lock className="h-4 w-4 mr-2" /> Tag abschließen
      </Button>
    )}
  </div>
);
